export class DrawingCommandHistory {
  constructor({ maximumEntries = 100, onAnnounce = null } = {}) {
    this.maximumEntries = Math.max(1, Number(maximumEntries) || 100);
    this.onAnnounce = onAnnounce;
    this.undoStack = [];
    this.redoStack = [];
  }

  execute({ label, targets, mutate }) {
    const uniqueTargets = [...new Set((targets || []).filter(Boolean))];
    if (uniqueTargets.length === 0 || typeof mutate !== "function") return false;
    const before = captureTargets(uniqueTargets);
    mutate();
    const after = captureTargets(uniqueTargets);
    return this.record({ label, before, after });
  }

  record({ label, before, after }) {
    if (!Array.isArray(before) || !Array.isArray(after) || statesEqual(before, after)) {
      return false;
    }
    this.undoStack.push({
      label: String(label || "Drawing change"),
      before,
      after
    });
    if (this.undoStack.length > this.maximumEntries) this.undoStack.shift();
    this.redoStack = [];
    return true;
  }

  recordCustom({ label, beforeKey, afterKey, undo, redo }) {
    if (
      typeof undo !== "function"
      || typeof redo !== "function"
      || beforeKey === afterKey
    ) return false;
    this.undoStack.push({
      label: String(label || "Chart change"),
      customUndo: undo,
      customRedo: redo
    });
    if (this.undoStack.length > this.maximumEntries) this.undoStack.shift();
    this.redoStack = [];
    return true;
  }

  undo() {
    const command = this.undoStack.pop();
    if (!command) return false;
    if (command.customUndo) command.customUndo();
    else restoreTargets(command.before);
    this.redoStack.push(command);
    this.onAnnounce?.(`Undid ${command.label.toLowerCase()}.`);
    return true;
  }

  redo() {
    const command = this.redoStack.pop();
    if (!command) return false;
    if (command.customRedo) command.customRedo();
    else restoreTargets(command.after);
    this.undoStack.push(command);
    this.onAnnounce?.(`Redid ${command.label.toLowerCase()}.`);
    return true;
  }

  clear() {
    this.undoStack = [];
    this.redoStack = [];
  }
}

export function captureDrawingTargets(targets) {
  return captureTargets([...new Set((targets || []).filter(Boolean))]);
}

function captureTargets(targets) {
  return targets.map((workspace) => ({
    workspace,
    snapshot: workspace.captureDrawingState()
  }));
}

function restoreTargets(entries) {
  for (const entry of entries) {
    entry.workspace?.restoreDrawingState(entry.snapshot);
  }
}

function statesEqual(before, after) {
  if (before.length !== after.length) return false;
  return before.every((entry, index) => (
    entry.workspace === after[index].workspace
    && JSON.stringify(entry.snapshot) === JSON.stringify(after[index].snapshot)
  ));
}
